import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { arrayRemoveAll, change, formValueSelector } from 'redux-form'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useApolloClient } from 'hooks/useApolloClient'
// components
import { IconButton } from 'components/UI'
import { ReduxEntitySelect } from 'components/FormElements'
// view components
import MappedProperties from '../MappedProperties'
// graphql
import { RISK_TEMPLATE_QUERY, RISK_TEMPLATE_SELECT_QUERY } from 'graphql/risk/queries'
// constants
import { Forms, Modals } from 'constants/index'
// views
import { ScheduleReviewIcon, TemplateSelectWrapper, Wrapper } from './views'
// utils
import get from 'lodash/get'
import { required } from 'utils/validators'
import { handleError } from 'utils/errors/handlers'
import { riskTemplatesProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

interface Props {
  field: string
}

const CreateSchedule: React.FC<Props> = ({ field }) => {
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const { showModal } = useShowModal({ modalType: Modals.SCHEDULED_RISK_RECURRENCE_MODAL })

  const recurrence = useSelector((state) => selector(state, `${field}.recurrence`))
  const riskTemplateId = useSelector((state) => selector(state, `${field}.riskTemplate`))?.value

  const [fields, setFields] = React.useState([])

  const getRiskTemplateFields = async (id: string) => {
    try {
      const riskTemplateFields = get(
        await client.query({
          fetchPolicy: 'no-cache',
          query: RISK_TEMPLATE_QUERY,
          variables: { filter: { ids: [id] }, pagination: { pageTake: 1, pageSkip: 0 } }
        }),
        'data.riskTemplates.data[0].fields'
      )

      setFields(riskTemplateFields)
    } catch (e) {
      handleError(e, 'Schedule.getRiskTemplateFields')
    }
  }

  const showRecurrenceSettings = () => showModal({ name: `${field}.recurrence` })

  const onTemplateChange = () => {
    dispatch(arrayRemoveAll(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.mappedProperties`))
    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.recurrence`, null))
    showRecurrenceSettings()
  }

  React.useEffect(() => {
    if (riskTemplateId) {
      getRiskTemplateFields(riskTemplateId)
    }
  }, [riskTemplateId])

  return (
    <Wrapper>
      <TemplateSelectWrapper>
        <ReduxEntitySelect
          label='Form'
          validate={required}
          menuPortalTarget={null}
          name={`${field}.riskTemplate`}
          onSelectChange={onTemplateChange}
          query={RISK_TEMPLATE_SELECT_QUERY}
          {...riskTemplatesProps}
        />
        {riskTemplateId && (
          <IconButton onClick={showRecurrenceSettings}>
            <ScheduleReviewIcon />
          </IconButton>
        )}
      </TemplateSelectWrapper>

      {riskTemplateId && recurrence && <MappedProperties fields={fields} name={`${field}.mappedProperties`} />}
    </Wrapper>
  )
}

export default CreateSchedule
